import { useEffect, useState } from "react";
import api from "../../lib/api";
import { DocumentList } from "../DocumentList";
import { StatCards } from "../StatCards";
import { DemoButton } from "../DemoButton";
import { AuditLogTable } from "../AuditLogTable";
import { AlertsPanel } from "../AlertsPanel";
import { CompliancePanel } from "../CompliancePanel";
import { DemoRequestsTable } from "../DemoRequestsTable";
import WaitlistAdminPanel from "../WaitlistAdminPanel";

const ROLE_BADGE = {
  family: "bg-[#E6F0EB] text-[#34745C]",
  agency: "bg-[#FDF3E1] text-[#C48231]",
  admin: "bg-[#D6E0D9] text-[#2E5C50]",
};

export default function AdminDashboard({ user, documents, stats, refresh, docTotal, loadMore }) {
  const [users, setUsers] = useState([]);

  useEffect(() => {
    api.get("/admin/users").then((res) => setUsers(res.data));
  }, [documents.length]);

  return (
    <div data-testid="admin-dashboard" className="space-y-10">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#C07A60]">Admin Console</p>
          <h1 className="mt-2 text-3xl md:text-4xl font-heading font-light tracking-tight">
            System overview, <span className="font-bold text-[#2E5C50]">{user.name}</span>
          </h1>
          <p className="mt-2 text-[#57605A]">Manage users, monitor documents and oversee system health.</p>
        </div>
        <DemoButton onSeeded={refresh} />
      </div>

      <StatCards stats={stats} />

      <CompliancePanel />

      <AlertsPanel />

      <WaitlistAdminPanel />

      <DemoRequestsTable />

      <div>
        <h2 className="text-lg font-heading font-semibold mb-4">Users</h2>
        <div data-testid="user-management-table" className="bg-white border border-[#E8E6E1] rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E8E6E1] bg-[#F9F7F4]">
                <th className="text-left px-5 py-3 text-xs uppercase tracking-[0.15em] font-semibold text-[#57605A]">Name</th>
                <th className="text-left px-5 py-3 text-xs uppercase tracking-[0.15em] font-semibold text-[#57605A]">Email</th>
                <th className="text-left px-5 py-3 text-xs uppercase tracking-[0.15em] font-semibold text-[#57605A]">Role</th>
                <th className="text-left px-5 py-3 text-xs uppercase tracking-[0.15em] font-semibold text-[#57605A] hidden md:table-cell">Joined</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-[#E8E6E1] last:border-0 hover:bg-[#F9F7F4]/60 transition-colors duration-150">
                  <td className="px-5 py-3 font-medium text-[#1C211F]">{u.name}</td>
                  <td className="px-5 py-3 text-[#57605A]">{u.email}</td>
                  <td className="px-5 py-3">
                    <span className={`${ROLE_BADGE[u.role]} px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wider`}>
                      {u.role}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-[#57605A] hidden md:table-cell">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <AuditLogTable />

      <div>
        <h2 className="text-lg font-heading font-semibold mb-4">All documents</h2>
        <DocumentList documents={documents} showUploader total={docTotal} onLoadMore={loadMore} />
      </div>
    </div>
  );
}
